import { useEffect, useMemo, useState } from "react";
import { FileText, Loader2, Search, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/**
 * 挑选采购核价模版 → 回传 { template, categoryDefaults } 给新建对话框，
 * 让字段按三层叠加（模版默认 → 种类默认覆盖 → 用户手改）自动填满新报价。
 *
 * 可选 ``productCategory`` 用于把该种类命中的模版排在最前，并自动预取
 * 对应的种类默认值 payload。
 */
export function TemplatePickerDialog({ open, onOpenChange, onPick, productCategory }) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [defaultsByCategory, setDefaultsByCategory] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [tplRes, catRes] = await Promise.all([
          api.get("/pq-templates"),
          api.get("/pq-category-defaults"),
        ]);
        if (cancelled) return;
        setItems(tplRes.data || []);
        const map = {};
        (catRes.data || []).forEach((d) => { map[d.category] = d.defaults || {}; });
        setDefaultsByCategory(map);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "加载模版失败");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (tpl) => !q
      || (tpl.name || "").toLowerCase().includes(q)
      || (tpl.remark || "").toLowerCase().includes(q)
      || (tpl.categories || []).some((c) => c.toLowerCase().includes(q));
    const hits = items.filter(match);
    // Templates matching the product's category float to the top.
    hits.sort((a, b) => {
      const ha = productCategory && (a.categories || []).includes(productCategory) ? 0 : 1;
      const hb = productCategory && (b.categories || []).includes(productCategory) ? 0 : 1;
      return ha - hb;
    });
    return hits;
  }, [items, query, productCategory]);

  const pick = (tpl) => {
    const categoryDefaults = productCategory ? (defaultsByCategory[productCategory] || {}) : {};
    onPick?.({ template: tpl, categoryDefaults });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col p-0" data-testid="pq-template-picker">
        <div className="p-4 border-b border-border">
          <DialogTitle className="flex items-center gap-2 mb-3">
            <FileText size={16} className="text-brand" /> 选择采购核价模版
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground mb-3">
            {productCategory
              ? `当前产品种类：${productCategory}（匹配模版排在最前）`
              : "搜索模版名称 / 备注 / 适用种类"}
          </DialogDescription>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索模版…"
              data-testid="pq-template-picker-search"
              className="pl-8 h-9"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="pq-template-picker-clear">
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 min-h-[300px]" data-testid="pq-template-picker-list">
          {loading && (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 size={18} className="animate-spin" />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-10">
              没有匹配的模版。管理员请先到「采购核价模版」页创建模版。
            </p>
          )}
          {!loading && filtered.map((tpl) => {
            const match = productCategory && (tpl.categories || []).includes(productCategory);
            return (
              <button
                key={tpl.id}
                type="button"
                onClick={() => pick(tpl)}
                data-testid={`pq-template-picker-row-${tpl.id}`}
                className={`w-full text-left px-3 py-2.5 rounded-md hover:bg-secondary/60 transition-colors ${
                  match ? "bg-brand/5 border border-brand/30" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate">{tpl.name || "(未命名)"}</span>
                  {match && <span className="text-[10px] text-brand font-semibold">当前种类匹配</span>}
                </div>
                {tpl.categories?.length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-0.5">适用：{tpl.categories.join("、")}</p>
                )}
                {tpl.remark && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{tpl.remark}</p>
                )}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
